/**
 * @module ui/pages/runs/test-runs/useTestRunsFilterState
 *
 * Filter state for the test-runs page: the URL search writer, the filter
 * values derived from it (target/status/triggered-by/date range), and the
 * target/triggered-by option catalogs harvested from whatever data has
 * already loaded (see the comment on the harvesting effects below for why —
 * neither filter has a closed enum).
 *
 * `buildTestRunsFilters` is exported separately (not returned by the hook)
 * because the page needs it *before* the hook can run: the trend and
 * runs-list queries need `TestRunsFilterParams` to fetch, and this hook's own
 * option-harvesting effects need those queries' results as input — so the
 * hook is called after the queries are constructed, using this same pure
 * function for the query params.
 */
import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { MultiSelectOption } from '@chatsdet/ui';
import type { TestRunsFilterParams } from '../../../../features/workflows/testRunsFilters';
import type { TestRunsTrendPoint } from './testRunsTrendChartData';
import type { RunsListRow } from '../../../../features/workflows/useWorkflowTestExecutions';
import type { DateRangeValue } from '../../../../utils/dateRange';
import { parseIsoToDateRange } from '../../../../utils/dateRange';
import type { TestRunsSearch } from '../runsRouteSearch';

/** Builds the shared runs-list/runs-trend filter params from the URL search state. */
export function buildTestRunsFilters(searchParams: TestRunsSearch): TestRunsFilterParams {
  const targetFilter = searchParams.targets ?? [];
  const statusFilter = searchParams.statuses ?? [];
  const triggeredByFilter = searchParams.triggeredBy ?? [];
  return {
    targets: targetFilter.length > 0 ? targetFilter : undefined,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
    triggeredBy: triggeredByFilter.length > 0 ? triggeredByFilter : undefined,
    startedAfter: searchParams.startedAfter,
    startedBefore: searchParams.startedBefore,
  };
}

export type TestRunsFilterState = {
  searchParams: TestRunsSearch;
  updateSearch: (updates: Partial<TestRunsSearch>) => void;
  targetFilter: string[];
  statusFilter: string[];
  triggeredByFilter: string[];
  dateRange: DateRangeValue;
  targetOptions: MultiSelectOption[];
  triggeredByOptions: MultiSelectOption[];
  targetLabelsRef: React.RefObject<Map<string, string>>;
  triggeredByLabelsRef: React.RefObject<Map<string, string>>;
};

/** Add run-by identities from any merged row source to the accumulated menu. */
function buildTriggeredByOptions(
  labels: Map<string, string>,
  executions: readonly RunsListRow[]
): MultiSelectOption[] {
  executions.forEach((execution) => {
    if (!execution.triggeredBy) return;
    labels.set(
      execution.triggeredBy.id,
      execution.triggeredBy.displayName ?? execution.triggeredBy.email
    );
  });
  return [
    { id: 'system', label: 'System' },
    ...Array.from(labels, ([id, label]) => ({ id, label })),
  ];
}

/**
 * @param trendPoints Current trend-query data, for harvesting target options.
 * @param executions Current runs-list data, for harvesting triggered-by options.
 */
export function useTestRunsFilterState({
  trendPoints,
  executions,
}: {
  trendPoints: TestRunsTrendPoint[] | undefined;
  executions: RunsListRow[] | undefined;
}): TestRunsFilterState {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/runs/test' }) as TestRunsSearch;

  const updateSearch = React.useCallback(
    (updates: Partial<TestRunsSearch>) =>
      navigate({
        to: '/runs/test',
        search: (prev: Record<string, unknown>) => {
          const prevParams = prev as TestRunsSearch;
          const merged: TestRunsSearch = { ...prevParams, ...updates };
          if (!merged.targets || merged.targets.length === 0) delete merged.targets;
          if (!merged.statuses || merged.statuses.length === 0) delete merged.statuses;
          if (!merged.triggeredBy || merged.triggeredBy.length === 0) delete merged.triggeredBy;
          if (!merged.startedAfter || !merged.startedBefore) {
            delete merged.startedAfter;
            delete merged.startedBefore;
          }
          if (!merged.sortBy) {
            delete merged.sortBy;
            delete merged.sortOrder;
          }
          return merged;
        },
      }),
    [navigate]
  );

  const targetFilter = searchParams.targets ?? [];
  const statusFilter = searchParams.statuses ?? [];
  const triggeredByFilter = searchParams.triggeredBy ?? [];
  const dateRange = React.useMemo(
    () => parseIsoToDateRange(searchParams.startedAfter, searchParams.startedBefore),
    [searchParams.startedAfter, searchParams.startedBefore]
  );

  // Filter-menu option catalogs. Target/Run-by have no closed enum (a target
  // is any suite/schedule/tag combination the org has ever run; "Run by" is
  // any org member who has triggered a run), so their options are harvested
  // from whatever data has already loaded — the trend window for targets, the
  // current runs page for triggered-by users — rather than a dedicated
  // endpoint. Accumulated (never shrunk) across responses so a value that's
  // currently selected keeps a readable label even if a narrower filter
  // combination would no longer surface it in the source data.
  const targetLabelsRef = React.useRef<Map<string, string>>(new Map());
  const [targetOptions, setTargetOptions] = React.useState<MultiSelectOption[]>([]);
  React.useEffect(() => {
    if (!trendPoints) return;
    trendPoints.forEach((point) => {
      targetLabelsRef.current.set(point.runTarget.key, point.runTarget.label);
    });
    setTargetOptions(Array.from(targetLabelsRef.current, ([id, label]) => ({ id, label })));
  }, [trendPoints]);

  const triggeredByLabelsRef = React.useRef<Map<string, string>>(new Map());
  const [triggeredByOptions, setTriggeredByOptions] = React.useState<MultiSelectOption[]>([
    { id: 'system', label: 'System' },
  ]);
  React.useEffect(() => {
    if (!executions) return;
    setTriggeredByOptions(buildTriggeredByOptions(triggeredByLabelsRef.current, executions));
  }, [executions]);

  return {
    searchParams,
    updateSearch,
    targetFilter,
    statusFilter,
    triggeredByFilter,
    dateRange,
    targetOptions,
    triggeredByOptions,
    targetLabelsRef,
    triggeredByLabelsRef,
  };
}
